import { StatusLoteRolloutFuncionarios } from './migrar-funcionarios.enum';

/**
 * Defines the possible tag colors for Nebular tags
 */
type ValeTagColor = 'basic' | 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'control' | string;

/**
 * Utility class for centralizing batch status logic
 * Provides labels, colors, and status categorization for batch operations
 */
export class MigracaoFuncionariosUtil {
  /**
   * Display labels for each batch status
   */
  private static readonly STATUS_LABELS: Record<StatusLoteRolloutFuncionarios, string> = {
    [StatusLoteRolloutFuncionarios.CREATED]: 'Criado',
    [StatusLoteRolloutFuncionarios.VALIDATING]: 'Validando',
    [StatusLoteRolloutFuncionarios.INVALID]: 'Erro planilha',
    [StatusLoteRolloutFuncionarios.OPEN]: 'Em aberto',
    [StatusLoteRolloutFuncionarios.SCHEDULED]: 'Agendado',
    [StatusLoteRolloutFuncionarios.CANCELLED]: 'Cancelado',
    [StatusLoteRolloutFuncionarios.WAITING_PROCESSING]: 'Processando',
    [StatusLoteRolloutFuncionarios.PROCESSING]: 'Processando',
    [StatusLoteRolloutFuncionarios.FAILED]: 'Erro importação',
    [StatusLoteRolloutFuncionarios.FINISHED]: 'Finalizado',
  };

  /**
   * Tag colors for each batch status
   */
  private static readonly STATUS_COLORS: Record<StatusLoteRolloutFuncionarios, ValeTagColor> = {
    [StatusLoteRolloutFuncionarios.CREATED]: 'processing',
    [StatusLoteRolloutFuncionarios.VALIDATING]: 'processing',
    [StatusLoteRolloutFuncionarios.INVALID]: 'error',
    [StatusLoteRolloutFuncionarios.OPEN]: 'default',
    [StatusLoteRolloutFuncionarios.SCHEDULED]: 'processing',
    [StatusLoteRolloutFuncionarios.CANCELLED]: 'default',
    [StatusLoteRolloutFuncionarios.WAITING_PROCESSING]: 'warning',
    [StatusLoteRolloutFuncionarios.PROCESSING]: 'warning',
    [StatusLoteRolloutFuncionarios.FAILED]: 'error',
    [StatusLoteRolloutFuncionarios.FINISHED]: 'success',
  };

  /**
   * Status that should stop polling (final states)
   */
  private static readonly FINAL_STATUSES: StatusLoteRolloutFuncionarios[] = [
    StatusLoteRolloutFuncionarios.INVALID,
    StatusLoteRolloutFuncionarios.OPEN,
    StatusLoteRolloutFuncionarios.SCHEDULED,
    StatusLoteRolloutFuncionarios.CANCELLED,
    StatusLoteRolloutFuncionarios.FAILED,
    StatusLoteRolloutFuncionarios.FINISHED,
  ];

  /**
   * Status where batch is being processed/validated (not editable)
   */
  private static readonly PROCESSING_STATUSES: StatusLoteRolloutFuncionarios[] = [StatusLoteRolloutFuncionarios.CREATED, StatusLoteRolloutFuncionarios.VALIDATING];

  /**
   * Status where user can edit the batch
   */
  private static readonly EDITABLE_STATUSES: StatusLoteRolloutFuncionarios[] = [
    StatusLoteRolloutFuncionarios.CREATED,
    StatusLoteRolloutFuncionarios.VALIDATING,
    StatusLoteRolloutFuncionarios.INVALID,
    StatusLoteRolloutFuncionarios.OPEN,
  ];

  /**
   * Status where user can cancel the batch
   */
  private static readonly CANCELABLE_STATUSES: StatusLoteRolloutFuncionarios[] = [StatusLoteRolloutFuncionarios.OPEN, StatusLoteRolloutFuncionarios.SCHEDULED];
  /**
   * Get display label for a batch status
   */
  static getStatusLabel(status: string | StatusLoteRolloutFuncionarios): string {
    const statusEnum = this.normalizeStatus(status);
    return this.STATUS_LABELS[statusEnum] || 'Desconhecido';
  }

  /**
   * Get tag color for a batch status
   */
  static getStatusColor(status: string | StatusLoteRolloutFuncionarios): ValeTagColor {
    const statusEnum = this.normalizeStatus(status);
    return this.STATUS_COLORS[statusEnum] || 'default';
  }

  /**
   * Check if status is final (should stop polling)
   */
  static isFinalStatus(status: string | StatusLoteRolloutFuncionarios): boolean {
    const statusEnum = this.normalizeStatus(status);
    return this.FINAL_STATUSES.includes(statusEnum);
  }

  /**
   * Check if status indicates batch is processing
   */
  static isProcessingStatus(status: string | StatusLoteRolloutFuncionarios): boolean {
    const statusEnum = this.normalizeStatus(status);
    return this.PROCESSING_STATUSES.includes(statusEnum);
  }

  /**
   * Check if batch can be edited in this status
   */
  static isEditableStatus(status: string | StatusLoteRolloutFuncionarios): boolean {
    const statusEnum = this.normalizeStatus(status);
    return this.EDITABLE_STATUSES.includes(statusEnum);
  }

  /**
   * Check if batch can be canceled in this status
   */
  static isCancelableStatus(status: string | StatusLoteRolloutFuncionarios): boolean {
    const statusEnum = this.normalizeStatus(status);
    return this.CANCELABLE_STATUSES.includes(statusEnum);
  }

  /**
   * Normalize status string to StatusLoteRolloutFuncionarios enum
   * Handles case-insensitive matching
   */
  private static normalizeStatus(status: string | StatusLoteRolloutFuncionarios): StatusLoteRolloutFuncionarios {
    const upperStatus = String(status || '').toUpperCase();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any
    return (StatusLoteRolloutFuncionarios as any)[upperStatus] || status as StatusLoteRolloutFuncionarios;
  }

  /**
   * Get all statuses for filter options
   */
  static getAllStatusOptions(): Array<{ label: string; value: StatusLoteRolloutFuncionarios }> {
    const allStatuses = [
      { label: this.getStatusLabel(StatusLoteRolloutFuncionarios.CREATED), value: StatusLoteRolloutFuncionarios.CREATED },
      { label: this.getStatusLabel(StatusLoteRolloutFuncionarios.VALIDATING), value: StatusLoteRolloutFuncionarios.VALIDATING },
      { label: this.getStatusLabel(StatusLoteRolloutFuncionarios.INVALID), value: StatusLoteRolloutFuncionarios.INVALID },
      { label: this.getStatusLabel(StatusLoteRolloutFuncionarios.OPEN), value: StatusLoteRolloutFuncionarios.OPEN },
      { label: this.getStatusLabel(StatusLoteRolloutFuncionarios.SCHEDULED), value: StatusLoteRolloutFuncionarios.SCHEDULED },
      { label: this.getStatusLabel(StatusLoteRolloutFuncionarios.CANCELLED), value: StatusLoteRolloutFuncionarios.CANCELLED },
      {
        label: this.getStatusLabel(StatusLoteRolloutFuncionarios.WAITING_PROCESSING),
        value: StatusLoteRolloutFuncionarios.WAITING_PROCESSING,
      },
      { label: this.getStatusLabel(StatusLoteRolloutFuncionarios.PROCESSING), value: StatusLoteRolloutFuncionarios.PROCESSING },
      { label: this.getStatusLabel(StatusLoteRolloutFuncionarios.FAILED), value: StatusLoteRolloutFuncionarios.FAILED },
      { label: this.getStatusLabel(StatusLoteRolloutFuncionarios.FINISHED), value: StatusLoteRolloutFuncionarios.FINISHED },
    ];

    return this.mergeProcessingStatusOption(allStatuses);
  }

  /**
   * Expand filter statuses to include related statuses
   * Ex: PROCESSING also includes WAITING_PROCESSING
   */
  static expandFilterStatuses(statuses?: string[]): string[] | undefined {
    if (!Array.isArray(statuses) || statuses.length === 0) return statuses;

    const expanded = new Set(statuses.map(s => String(s).toUpperCase()));

    // If PROCESSING is selected, also include WAITING_PROCESSING
    if (expanded.has(StatusLoteRolloutFuncionarios.PROCESSING)) {
      expanded.add(StatusLoteRolloutFuncionarios.WAITING_PROCESSING);
    }

    // If WAITING_PROCESSING is selected, also include PROCESSING
    if (expanded.has(StatusLoteRolloutFuncionarios.WAITING_PROCESSING)) {
      expanded.add(StatusLoteRolloutFuncionarios.PROCESSING);
    }

    return Array.from(expanded);
  }

  /**
   * Hide WAITING_PROCESSING option for beneficiaries filters since it is grouped under PROCESSING
   */
  private static mergeProcessingStatusOption(
    statuses: Array<{ label: string; value: StatusLoteRolloutFuncionarios }>
  ): Array<{ label: string; value: StatusLoteRolloutFuncionarios }> {
    return statuses.filter(status => status.value !== StatusLoteRolloutFuncionarios.WAITING_PROCESSING);
  }
}
